import { useState, useCallback, useRef } from 'react';
import {
  getConversationMessages,
  sendMessage as sendMessageService,
} from '../../../../../services/conversations';

/**
 * Hook that handles messages for a specific conversation.
 * Calls the service getConversationMessages() directly; keeps local state only (no global store).
 * Same pattern as useHomeChatConversations: shared services + local module state.
 */
export const useHomeChatMessages = () => {
  const [messages, setMessages] = useState([]);
  const [agentIsTyping, setAgentIsTyping] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);
  const isSendingRef = useRef(false);

  /**
   * Fetches messages for a specific conversation from the service.
   * Ignores responses for older requests when switching conversations quickly.
   * @param {string} conversationId - Conversation UUID
   * @returns {Promise<Array>} Mapped messages list
   */
  const fetchMessages = useCallback(async (conversationId) => {
    if (!conversationId) {
      setMessages([]);
      setError(null);
      return [];
    }
    const requestId = ++fetchIdRef.current;
    try {
      setIsLoadingMessages(true);
      setError(null);
      setMessages([]);

      const response = await getConversationMessages({ conversationId });
      if (requestId !== fetchIdRef.current) return [];

      // API returns { statusCode, body: [...], meta } — body is the messages array
      const list = Array.isArray(response.body)
        ? response.body
        : Array.isArray(response.messages)
          ? response.messages
          : Array.isArray(response.data)
            ? response.data
            : [];

      const mapped = list
        .sort((a, b) => {
          const dateA = new Date(a.createdAt || a.lastUpdated || a.timestamp || 0).getTime();
          const dateB = new Date(b.createdAt || b.lastUpdated || b.timestamp || 0).getTime();
          return dateA - dateB; // Oldest first for chat messages
        })
        .map((msg) => {
          // API may return text in multiple fields (see HistoryAgentStore)
          const content =
            msg.formattedText ??
            msg.messageText ??
            msg.message ??
            msg.content ??
            msg.text ??
            msg.body ??
            '';
          // Normalize role: API uses senderRole "USER"|"AGENT" or direction "INBOUND"|"OUTBOUND"
          const senderRole = (msg.senderRole || msg.role || '').toUpperCase();
          const direction = (msg.direction || '').toUpperCase();
          const isUser =
            senderRole === 'USER' ||
            direction === 'INBOUND' ||
            msg.role === 'user' ||
            msg.sender === 'user';
          const role = isUser ? 'user' : msg.role || msg.sender || 'assistant';

          return {
            id: msg.messageUuid || msg.id || msg.uuid,
            uuid: msg.messageUuid || msg.id || msg.uuid,
            messageUuid: msg.messageUuid || msg.id || msg.uuid,
            content: typeof content === 'string' ? content : String(content || ''),
            role,
            createdAt: msg.createdAt || msg.timestamp,
            timestamp: msg.createdAt || msg.timestamp,
            conversationId: conversationId,
            attachments: msg.attachments || [],
            metadata: msg.metadata || {},
          };
        });

      if (requestId !== fetchIdRef.current) return [];
      setMessages(mapped);
      return mapped;
    } catch (err) {
      if (requestId === fetchIdRef.current) {
        console.error('Error fetching conversation messages:', err);
        setError(err);
        setMessages([]);
      }
      return [];
    } finally {
      if (requestId === fetchIdRef.current) {
        setIsLoadingMessages(false);
      }
    }
  }, []);

  const cleanThread = useCallback(() => {
    setMessages([]);
    setAgentIsTyping(false);
    setError(null);
  }, []);

  /**
   * Sends a message and refreshes the thread. Call with conversationId and agentId from context.
   * @param {string} messageText - Text to send
   * @param {Object} [options] - { conversationId, agentId, fileUuids, attachmentDetails, llmConfig, context }
   * @param {Array} [options.attachmentDetails] - For optimistic UI: [{ fileUuid, name, fileType, isLoading }]
   * @param {Object} [options.llmConfig] - Optional { llm_model, llm_model_provider } for model selection
   * @param {Object} [options.context] - Injected context (location, entity, entityValue, scope) — Story 7. scope dentro de context.
   */
  const sendMessage = useCallback(async (messageText, options = {}) => {
    const { conversationId, agentId, fileUuids, attachmentDetails, llmConfig, context } = options;
    const trimmed = typeof messageText === "string" ? messageText.trim() : "";
    if (!trimmed) return;
    if (isSendingRef.current) return;

    isSendingRef.current = true;
    setError(null);
    setAgentIsTyping(true);

    const hasFileUuids = Array.isArray(fileUuids) && fileUuids.length > 0;
    const hasDetails = Array.isArray(attachmentDetails) && attachmentDetails.length > 0;
    const optimisticAttachments = hasDetails
      ? attachmentDetails.map((a) => ({
          fileUuid: a.fileUuid ?? a.document_uuid,
          name: a.name ?? "Document",
          fileType: a.fileType ?? "",
          isLoading: a.isLoading === true,
        }))
      : hasFileUuids
        ? fileUuids.map((uuid) => ({ fileUuid: uuid, name: "Document", fileType: "", isLoading: false }))
        : [];

    const optimisticUserMessage = {
      id: `opt-${Date.now()}`,
      uuid: `opt-${Date.now()}`,
      messageUuid: `opt-${Date.now()}`,
      content: trimmed,
      role: "user",
      createdAt: new Date().toISOString(),
      timestamp: new Date().toISOString(),
      conversationId: conversationId || null,
      attachments: optimisticAttachments,
      metadata: {},
    };
    setMessages((prev) => [...prev, optimisticUserMessage]);

    try {
      const response = await sendMessageService({
        messageText: trimmed,
        conversationUuid: conversationId,
        agentUuid: agentId,
        channel: "WEB",
        fileUuids:
          Array.isArray(fileUuids) && fileUuids.length > 0
            ? fileUuids
            : undefined,
        ...(llmConfig && typeof llmConfig === "object" && { llmConfig }),
        ...(context && typeof context === "object" && { context }),
      });
      return response;
    } catch (err) {
      console.error("Error sending message:", err);
      setError(err);
      return null;
    } finally {
      isSendingRef.current = false;
    }
  }, []);

  return {
    messages,
    setMessages,
    agentIsTyping,
    setAgentIsTyping,
    isLoadingMessages,
    setIsLoadingMessages,
    error,
    setError,
    fetchMessages,
    cleanThread,
    sendMessage,
  };
};
